// @file Whiteboard-specific AI chat surface strategy.
// Consumed by useAIChatSurfaceStrategy factory — use that composable, not this one directly.
import { __ } from '@@/bits/intl'
import type { Suggestion } from '@@/pinia/surface_ai_chat_store'
import { computed } from 'vue'
import type { AIChatPersonalizedTool, AIChatSurfaceStrategyReturn } from './useAIChatSurfaceStrategy'

function buildWhiteboardFallbackSuggestions(): Suggestion[] {
  const createdAt = Date.now()
  return [
    {
      short_message: __('What can you help me with here?'),
      long_message: __('What can you help me with on this sandbox?'),
      created_at: createdAt,
    },
    {
      short_message: __('Suggest better padlet settings.'),
      long_message: __('Suggest a better title, description, and wallpaper for this sandbox.'),
      created_at: createdAt,
    },
    {
      short_message: __('What can you change for me?'),
      long_message: __('What wall-level settings can you change for this sandbox?'),
      created_at: createdAt,
    },
  ]
}

function useWhiteboardAIChatSurfaceStrategy(): AIChatSurfaceStrategyReturn {
  const surfaceType = 'whiteboard' as const

  const introduction = {
    title: __('Padlet Intelligence'),
    description: __('Update your sandbox title, wallpaper, and other settings through our chat interface.'),
    manualMessagePlaceholder: __('Suggest a better title and wallpaper for my sandbox.'),
  }

  const fallbackSuggestions = buildWhiteboardFallbackSuggestions()

  const shouldFetchSuggestionsFromApi = computed<boolean>(() => true)

  const personalizedTools = computed<AIChatPersonalizedTool[]>(() => [])

  return {
    surfaceType,
    introduction,
    fallbackSuggestions,
    shouldFetchSuggestionsFromApi,
    personalizedTools,
  }
}

export { useWhiteboardAIChatSurfaceStrategy }
